import { CircularProgress } from '@mui/material';

import Image from 'next/image';
import { type ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import { defaultItemThumbnail } from '../utils/common/variables';
import { UImageService } from '../utils/service/UImageService';

type UImageProps = Omit<
  ComponentPropsWithoutRef<typeof Image>,
  'alt' | 'width' | 'height'
> & {
  alt?: string;
  width: number | `${number}`;
  height: number | `${number}`;
  /** 이미지 로드 실패 시 표시할 대체 이미지 URL (기본값: defaultItemThumbnail) */
  fallbackSrc?: string;
};
/**
 * 기본 UImage 컴포넌트
 * - 이미지 로드 실패 시 fallbackSrc로 대체
 * - Next.js Image 컴포넌트 래핑
 */
// eslint-disable-next-line react/display-name
const UImage = forwardRef<HTMLImageElement, UImageProps>((props, ref?) => {
  const {
    fallbackSrc = defaultItemThumbnail,
    width,
    height,
    loader: loaderProp,
    ...restProps
  } = props;
  /** Next.js Image는 src가 비어 있으면 에러 발생 → 초기값 보정 */
  const initialSrc = String(props.src).trim() !== '' ? props.src : fallbackSrc;
  const [imgSrc, setImgSrc] = useState(initialSrc);
  const [hasError, setHasError] = useState(false);
  const [prevSrc, setPrevSrc] = useState(props.src);

  // props.src가 바뀌면 내부 state를 동기화한다. imgSrc/hasError를 state로 들고 있어
  // props.src 변경이 자동 반영되지 않으므로, 렌더 중 setState로 동기화해 호출처의
  // key 강제 remount 없이 src 교체·에러 상태 초기화가 동작하게 한다.
  if (props.src !== prevSrc) {
    setPrevSrc(props.src);
    setImgSrc(initialSrc);
    setHasError(false);
  }

  const handleError = () => {
    if (!hasError && imgSrc !== fallbackSrc) {
      setHasError(true);
      setImgSrc(fallbackSrc);
    }
  };

  const isSquare = Number(width) === Number(height);
  const loader = loaderProp ?? UImageService.defaultLoader({ isSquare });

  return (
    <Image
      {...restProps}
      src={imgSrc}
      width={width}
      height={height}
      ref={ref}
      alt={props.alt ?? ''}
      onError={handleError}
      loader={loader}
    />
  );
});

/**
 * 로딩 인디케이터가 있는 UImage 컴포넌트
 * - UImage를 래핑하여 이미지 로딩 중 CircularProgress 표시
 * - 로딩 완료 시 fade-in 효과 적용
 */
// eslint-disable-next-line react/display-name
export const UImageWithLoading = forwardRef<
  HTMLImageElement,
  UImageProps & { enabled?: boolean }
>((props, ref?) => {
  const { enabled = true, style, ...restProps } = props;
  const [isImageLoading, setIsImageLoading] = useState(enabled);

  return (
    <div className="relative">
      {isImageLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <CircularProgress
            size={40}
            color="inherit"
            className="text-orange-500"
          />
        </div>
      )}
      <UImage
        {...restProps}
        ref={ref}
        onLoad={() => setIsImageLoading(false)}
        style={{
          opacity: isImageLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          ...style,
        }}
      />
    </div>
  );
});
export default UImage;
